import { useState } from "react";
import { Smile, Hand } from "lucide-react";
import { cn } from "@vc/lib/utils";
import { useTranslation } from "@/clientWeb/i18n/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/clientWeb/components/ui/dropdown-menu";
import { ControlButton } from "../common/ControlButton";
import { useMeetingSessionStore } from "@/features/meeting/store/meeting-session.store";

interface ReactionControlProps {
  onReact?: (emoji: string) => void;
  compact?: boolean;
  /** Host-controls gate: when false, the raise-hand sub-button is hidden so
   *  attendees can't raise their hand. Prop-driven from the feature layer. */
  canRaiseHand?: boolean;
}

const REACTION_EMOJIS = [
  "👍", "👏", "😂", "😢", "🔥", "🎉",
  "😍", "😮", "🙌", "👌", "🙏", "👎",
];

export function ReactionControl({ onReact, compact = false, canRaiseHand = true }: ReactionControlProps) {
  const t = useTranslation();
  const [reactionDropdownOpen, setReactionDropdownOpen] = useState(false);
  const handRaised = useMeetingSessionStore((s) => s.handRaised);
  const onToggleHand = useMeetingSessionStore((s) => s.toggleHand);

  return (
    <>
      <div className="group flex gap-2 items-center rounded-full">
        {canRaiseHand && (
          <ControlButton
            active={handRaised}
            isToggle
            onClick={() => onToggleHand?.()}
            label={handRaised ? t("controlBar.lowerHand") : t("controlBar.raiseHand")}
            compact={compact}
            // A raised hand needs to be obvious to the person who raised it.
            // ControlButton's generic `active` look is the SAME colour as its
            // resting state at 20% opacity with a ring in that same hue, so the
            // raised button read as fainter than the lowered one — the opposite
            // of an indicator. Amber is what a raised hand already looks like
            // everywhere else in the app (the tile badge's amber glow, the
            // participants list), so the control bar now matches instead of
            // inventing a third treatment.
            className={
              handRaised
                ? "bg-amber-500 ring-2 ring-amber-300/60 hover:bg-amber-600"
                : undefined
            }
          >
            <Hand
              className={cn("text-white", compact ? "h-4 w-4" : "h-5 w-5")}
              // lucide's Hand is four OPEN stroke paths (finger arcs plus a palm
              // that ends mid-curve), so `fill` would implicitly close each one
              // and paint overlapping blobs rather than a filled hand. The solid
              // amber button is what carries "filled" here.
              strokeWidth={handRaised ? 2.5 : 2}
            />
          </ControlButton>
        )}

        <DropdownMenu open={reactionDropdownOpen} onOpenChange={setReactionDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <div>
              <ControlButton onClick={() => { }} label={t("controlBar.sendReaction")} compact={compact}>
                <Smile className={cn("text-white", compact ? "h-4 w-4" : "h-5 w-5")} />
              </ControlButton>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="min-w-[220px]">
            <div className="grid grid-cols-6 p-2">
              {REACTION_EMOJIS.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => {
                    onReact?.(e);
                    setReactionDropdownOpen(false);
                  }}
                  className="
                    group
                    relative
                    grid h-10 w-10 place-items-center
                    rounded-xl
                    text-xl
                    cursor-pointer
                    transition-all duration-200 ease-out
                    hover:bg-slate-100 dark:hover:bg-white/10
                    hover:scale-125
                    hover:-translate-y-1
                    active:scale-110
                  "
                >
                  <span
                    className="
                      transition-transform duration-200
                      group-hover:scale-110
                    "
                  >
                    {e}
                  </span>
                </button>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}
